import { CheckCircle, Maximize2, Play, Video, X, XCircle } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  CardRemoveIcon,
  EditIcon,
  MessageIcon,
  PauseIcon,
  UserIcon,
} from '@/components/Icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import {
  getAdvertisementDetail,
  updateAdvertisementAcknowledgement,
  updateAdvertisementStatus,
} from '@/api/sponsorApi'
import {
  AdvertisementStatus,
  type AdvertisementData,
} from '@/schemas/sponsor.schems'
import { formatDateDisplay } from '@/utils/dateUtils'
import CustomLoader from '../CustomLoader'
import {
  ADID_PARAM,
  AdvertisementActions,
  EVENT_MEDIA_OPTIONS,
  getAdvertisementStatusBadgeProps,
  getMediaType,
  transformAdvertisementPlacement,
  type AdvertisementActionsType,
} from '@/constants/sponsor'
import { useState } from 'react'
import { queryClient } from '@/main'
import { showSuccessToast } from '../ui/toast'
import { getPlainText, handleAxiosError } from '@/utils/helper'
import { UserActionDialog } from '../UserActionDialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import EditAdPlacementDrawer from './EditAdPlacementDrawer'
import { useNavigate, useSearchParams } from 'react-router-dom'
import HomeHeroSectionBanner, {
  type BannerData,
} from '../sponsorship/banners/HomeHeroSectionBanner'
import HomeInFeedNativeCardBanner from '../sponsorship/banners/HomeInFeedNativeCardBanner'
import InlineBanner from '../sponsorship/banners/InlineBanner'
import { PATHROUTES } from '@/constants/path'
import { useUserRole } from '@/contexts/UserRoleContext'

interface AdPreviewDrawerProps {
  readonly adId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const bannerComponents: Record<
  string,
  React.FC<{ data: BannerData }> | undefined
> = {
  hero: HomeHeroSectionBanner,
  in_feed_bursts: HomeInFeedNativeCardBanner,
  right_rail: InlineBanner,
}

const getBannerComponent = (placement?: string) => {
  if (!placement) return null
  return bannerComponents[placement] || null
}

// Status badge component
function StatusBadge({
  status,
  isChangesRequired,
}: Readonly<{
  status: string
  isChangesRequired: boolean
}>) {
  const badgeProps = getAdvertisementStatusBadgeProps(status, isChangesRequired)
  return (
    <Badge
      variant="outline"
      className={cn(
        'px-2.5 py-1 text-xs font-medium rounded-full',
        badgeProps.className
      )}
    >
      {badgeProps.children}
    </Badge>
  )
}

export default function AdPreviewDrawer({
  adId,
  open,
  onOpenChange,
}: Readonly<AdPreviewDrawerProps>) {
  const [userActionDialogOpen, setUserActionDialogOpen] = useState(false)
  const [userActionType, setUserActionType] =
    useState<AdvertisementActionsType | null>(null)
  const [editAdplacementDrawerOpen, setEditAdplacementDrawerOpen] =
    useState(false)
  const [isFullScreenPreviewOpen, setIsFullScreenPreviewOpen] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { currentUser } = useUserRole()
  const isMessageAdmin = currentUser?.isChatAdmin

  const removeAdIdParam = () => {
    if (searchParams.has(ADID_PARAM)) {
      searchParams.delete(ADID_PARAM)
      setSearchParams(searchParams)
    }
  }
  const {
    data: advertisementData,
    isLoading: isLoadingAdData,
    refetch: refetchAdData,
  } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.advertisementById(adId),
    queryFn: () => getAdvertisementDetail({ id: adId }),
    enabled: !!adId && open,
  })

  const adData: AdvertisementData = advertisementData?.data
  const adStatus = adData?.status
  const isActive = adStatus === AdvertisementStatus.ACTIVE
  const isPending = adStatus === AdvertisementStatus.INACTIVE

  const BannerComponent = getBannerComponent(adData?.placement)

  let dialogTitle = ''
  let dialogSubtext = ''
  let dialogButton = ''
  let dialogDanger = false
  if (userActionType === AdvertisementActions.approve) {
    dialogTitle = 'Are you sure you want to approve this ad?'
    dialogSubtext =
      'Once approved, it will be visible on the website as per the scheduled placement.'
    dialogButton = 'Approve Ad'
  } else if (userActionType === AdvertisementActions.reject) {
    dialogTitle = 'Are you sure you want to reject this ad?'
    dialogSubtext =
      'Please provide a reason so the sponsor can make necessary changes.'
    dialogButton = 'Reject Ad'
    dialogDanger = true
  } else if (userActionType === AdvertisementActions.delete) {
    dialogTitle = 'Are you sure you want to permanently remove this ad?'
    dialogSubtext =
      'This action cannot be undone, and the sponsor will be notified.'
    dialogButton = 'Remove Ad'
    dialogDanger = true
  } else if (userActionType === AdvertisementActions.active) {
    dialogTitle = 'Are you sure you want to resume this Ad?'
    dialogSubtext =
      'Resuming will immediately make this ad live again on the event website. Performance tracking will continue from where it left off.'
    dialogButton = 'Resume Ad'
  } else if (userActionType === AdvertisementActions.inactive) {
    dialogTitle = 'Are you sure you want to pause this Ad?'
    dialogSubtext =
      'Pausing will immediately stop this ad from displaying on the event website. You can resume it anytime from your dashboard.'
    dialogButton = 'Pause Ad'
    dialogDanger = true
  }

  const {
    mutate: updateAdvertisementStatusMutation,
    isPending: isUpdatingAdvertisementStatus,
  } = useMutation({
    mutationFn: updateAdvertisementStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementList],
      })
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementById(adId)],
      })
      refetchAdData()

      setUserActionDialogOpen(false)
      let toastTitle = ''
      let toastDesc = ''
      if (userActionType === AdvertisementActions.active) {
        toastTitle = 'Ad Restarted'
        toastDesc =
          'Your ad is now live again on the event website. You can monitor its performance in your dashboard.'
      } else if (userActionType === AdvertisementActions.inactive) {
        toastTitle = 'Ad Paused'
        toastDesc =
          'This ad has been successfully paused and is no longer visible on the event website. You can resume it anytime from your dashboard.'
      } else if (userActionType === AdvertisementActions.delete) {
        toastTitle = 'The ad has been removed successfully.'
        toastDesc = 'The sponsor has been notified.'
      }
      showSuccessToast(toastTitle, toastDesc)
      setUserActionType(null)
    },
    onError: error => {
      handleAxiosError('Failed to update advertisement status', error)
    },
  })

  const {
    mutate: updateAdvertisementACKMutation,
    isPending: isUpdatingAdvertisementACK,
  } = useMutation({
    mutationFn: updateAdvertisementAcknowledgement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementList],
      })
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementById(adId)],
      })
      refetchAdData()

      setUserActionDialogOpen(false)
      let toastTitle = ''
      let toastDesc = ''
      if (userActionType === AdvertisementActions.approve) {
        toastTitle = 'The ad has been approved successfully.'
        toastDesc = ''
      } else if (userActionType === AdvertisementActions.reject) {
        toastTitle = 'The ad has been rejected successfully.'
        toastDesc = 'The sponsor has been notified with your provided reason.'
      }
      showSuccessToast(toastTitle, toastDesc)
      setUserActionType(null)
    },
    onError: error => {
      handleAxiosError('Failed to update advertisement acknowledgement', error)
    },
  })

  const handleActionClick = async (
    action: AdvertisementActionsType,
    reason?: string
  ) => {
    const payload = {
      status: action,
      ...(reason && { reason }),
    }
    if (
      action === AdvertisementActions.approve ||
      action === AdvertisementActions.reject
    ) {
      updateAdvertisementACKMutation({
        id: adId,
        payload,
      })
    } else {
      updateAdvertisementStatusMutation({
        id: adId,
        payload,
      })
    }
  }

  const handleSetAction = (action: AdvertisementActionsType) => {
    setUserActionType(action)
    setUserActionDialogOpen(true)
  }

  const handleMessageAdminClick = () => {
    navigate(PATHROUTES.DASHBOARD.MESSAGE_ADMIN_WITH_ID(adData?.sponsorId), {
      state: {
        breadcrumbText: 'Sponsor Ad Management',
      },
    })
  }

  const getActionButtons = () => {
    switch (adStatus) {
      case AdvertisementStatus.DRAFT: {
        return (
          <>
            <Button
              variant={'outline'}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-positive-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.approve)}
            >
              <CheckCircle className="h-4 w-4" />
              Approve Ad
            </Button>
            <Button
              variant={'outline'}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-critical-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.reject)}
            >
              <XCircle className="h-4 w-4" />
              Reject Ad
            </Button>
          </>
        )
      }
      case AdvertisementStatus.ACTIVE:
      case AdvertisementStatus.INACTIVE: {
        return (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className=" border-ss-critical-200 bg-transparent text-ss-critical-200 hover:text-ss-critical-200 hover:border-ss-critical-200 hover:bg-ss-neutral-25/70"
                  aria-label="Delete advertisement"
                  onClick={() => handleSetAction(AdvertisementActions.delete)}
                >
                  <CardRemoveIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                Remove Ad
              </TooltipContent>
            </Tooltip>
            {isActive && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className=" border-ss-critical-200 bg-transparent text-ss-critical-200 hover:text-ss-critical-200 hover:border-ss-critical-200 hover:bg-ss-neutral-25/70"
                    aria-label="Pause advertisement"
                    onClick={() =>
                      handleSetAction(AdvertisementActions.inactive)
                    }
                  >
                    <PauseIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="center">
                  Pause Ad
                </TooltipContent>
              </Tooltip>
            )}
            {isPending && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className=" border-ss-positive-200 bg-transparent text-ss-positive-200 hover:text-ss-positive-200 hover:border-ss-positive-200 hover:bg-ss-neutral-25/70"
                    aria-label="Resume advertisement"
                    onClick={() => handleSetAction(AdvertisementActions.active)}
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="center">
                  Resume Ad
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'inline-flex',
                    !isMessageAdmin && 'cursor-not-allowed'
                  )}
                  role="button"
                  aria-disabled={!isMessageAdmin}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Message advertiser"
                    disabled={!isMessageAdmin}
                    onClick={handleMessageAdminClick}
                  >
                    <MessageIcon className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                {isMessageAdmin
                  ? 'Message Sponsor'
                  : "Don't have access to message sponsor"}
              </TooltipContent>
            </Tooltip>
            <Button
              size="sm"
              onClick={() => setEditAdplacementDrawerOpen(true)}
            >
              <EditIcon className="h-4 w-4 mr-1" />
              Edit Ad Placement
            </Button>
          </>
        )
      }
      case AdvertisementStatus.REJECTED: {
        return (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className=" border-ss-critical-200 bg-transparent text-ss-critical-200 hover:text-ss-critical-200 hover:border-ss-critical-200 hover:bg-ss-neutral-25/70"
                  aria-label="Delete advertisement"
                  onClick={() => handleSetAction(AdvertisementActions.delete)}
                >
                  <CardRemoveIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                Remove Ad
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'inline-flex',
                    !isMessageAdmin && 'cursor-not-allowed'
                  )}
                  role="button"
                  aria-disabled={!isMessageAdmin}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Message advertiser"
                    disabled={!isMessageAdmin}
                    onClick={handleMessageAdminClick}
                  >
                    <MessageIcon className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                {isMessageAdmin
                  ? 'Message Sponsor'
                  : "Don't have access to message sponsor"}
              </TooltipContent>
            </Tooltip>
          </>
        )
      }
      default: {
        return null
      }
    }
  }

  const getAdImagePreview = () => {
    if (!adData?.eventImage) {
      return null
    }

    const mediaType = getMediaType(adData?.eventImage?.key)
    const url = adData?.eventImage?.url
    return (
      <>
        {mediaType === EVENT_MEDIA_OPTIONS.VIDEO ? (
          <div className="relative size-16 rounded-md border border-ss-neutral-100 bg-ss-neutral-50 flex items-center justify-center overflow-hidden">
            <video
              src={url}
              className="size-full object-cover"
              muted
              playsInline
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <Video className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
          </div>
        ) : (
          <img
            src={url}
            alt={adData?.name}
            className="w-full h-full object-cover rounded"
          />
        )}
      </>
    )
  }

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={open => {
          if (!open) {
            removeAdIdParam()
          }
          onOpenChange(open)
        }}
        direction="right"
      >
        <DrawerContent
          aria-labelledby="ad-details-title"
          aria-label="Advertisement details drawer"
          className="h-full p-0 w-full max-w-max lg:max-w-[50%] rounded-none lg:rounded-xl"
        >
          <DrawerHeader className="border-b border-ss-neutral-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <DrawerTitle
                id="ad-details-title"
                className="text-ss-text-primary text-lg font-semibold leading-7"
              >
                Advertisement Details
              </DrawerTitle>
              <DrawerClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Close advertisement details drawer"
                  className="h-5 w-5"
                >
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          {isLoadingAdData || !adData ? (
            <CustomLoader />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Ad Header Section - matches Figma exactly */}
                <div className="flex items-center gap-2.5 flex-col md:flex-row">
                  {/* Ad Image - 64x64 as per Figma */}
                  <div className="min-w-16 h-16 bg-ss-neutral-200 rounded">
                    {adData?.eventImage ? (
                      getAdImagePreview()
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-ss-blue-100 to-ss-blue-300 rounded flex items-center justify-center">
                        <span className="text-ss-blue-700 font-bold text-sm">
                          <UserIcon />
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Ad Title and Status */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h3 className="text-ss-text-primary text-base font-semibold leading-[22px] break-all max-w-[400px]">
                        {getPlainText(adData?.name)}
                      </h3>
                      <StatusBadge
                        status={adStatus ?? ''}
                        isChangesRequired={adData?.isChangesRequired}
                      />
                    </div>
                    <p className="text-ss-text-secondary text-sm leading-4.5 ">
                      {adData?.event?.name}
                    </p>
                  </div>

                  {/* Action Buttons - following Figma exactly */}
                  <div className="flex items-center gap-2.5 flex-wrap">
                    {getActionButtons()}
                  </div>
                </div>

                {/* Separator */}
                <div className="bg-ss-neutral-100 h-px w-full" />

                {/* Description Section */}
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <span className="text-ss-text-secondary text-sm leading-4.5 ">
                      Advertisement Description
                    </span>
                    <p className="text-ss-text-primary text-base font-semibold leading-[22px] whitespace-pre-wrap">
                      {getPlainText(adData?.description)}
                    </p>
                  </div>
                </div>

                {/* Details Grid - 5 column grid as per Figma */}
                <div className="space-y-3">
                  <div className="gap-4 md:gap-12 grid grid-cols-1 md:grid-cols-[130px_130px_auto]  w-full">
                    {/* Row 1, Column 1 */}
                    <div className="space-y-1.5">
                      <span className="text-ss-text-secondary text-sm leading-4.5 ">
                        Ad Start Date
                      </span>
                      <p className="text-ss-text-primary text-base font-semibold leading-[22px] break-all">
                        {formatDateDisplay(adData?.startDate)}
                      </p>
                    </div>

                    {/* Row 1, Column 2 */}
                    <div className="space-y-1.5">
                      <span className="text-ss-text-secondary text-sm leading-4.5 ">
                        Ad End Date
                      </span>
                      <p className="text-ss-text-primary text-base font-semibold leading-[22px] break-all">
                        {formatDateDisplay(adData?.endDate)}
                      </p>
                    </div>

                    {/* Row 1, Column 3 */}
                    <div className="space-y-1.5">
                      <span className="text-ss-text-secondary text-sm leading-4.5 ">
                        Advertisement Redirection Link
                      </span>
                      <p className="text-ss-text-primary text-base font-semibold leading-[22px] break-all">
                        {adData?.link}
                      </p>
                    </div>
                  </div>
                  <div className="gap-4 md:gap-12 grid grid-cols-1  md:grid-cols-[230px_auto_auto]  w-full">
                    {/* Row 2, Column 1 */}
                    <div className="space-y-1.5">
                      <span className="text-ss-text-secondary text-sm leading-4.5 ">
                        Advertisement Link Text
                      </span>
                      <p className="text-ss-text-primary text-base font-semibold leading-[22px] break-all">
                        {adData?.linkText}
                      </p>
                    </div>

                    {/* Row 2, Column 3 */}
                    <div className="space-y-1.5">
                      <span className="text-ss-text-secondary text-sm leading-4.5 ">
                        Ad Type & Placement
                      </span>
                      <p className="text-ss-text-primary text-base font-semibold leading-[22px] break-all">
                        {transformAdvertisementPlacement(adData?.placement)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Advertisement Preview Section */}
                {BannerComponent && (
                  <div className="space-y-3">
                    <div className="bg-ss-neutral-100 h-px w-full" />

                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-ss-text-primary text-base font-semibold leading-[22px]">
                          Advertisement Preview
                        </h4>
                      </div>
                      <div className="p-4 overflow-x-auto">
                        {adData?.placement === 'hero' ? (
                          <div className="flex items-center justify-center py-20">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setIsFullScreenPreviewOpen(true)}
                              className="h-9 px-3 gap-2"
                            >
                              <Maximize2 className="w-4 h-4" />
                              View Full Screen
                            </Button>
                          </div>
                        ) : (
                          <BannerComponent
                            data={{
                              adName: adData?.name,
                              adDescription: adData?.description,
                              linkText: adData?.linkText,
                              redirectionLink: adData?.link,
                              sponsorLogoUrl: adData?.sponsorLogo?.url || null,
                              eventImageUrl: adData?.eventImage?.url || null,
                              eventMediaType:
                                adData?.eventImage &&
                                getMediaType(adData.eventImage.key) ===
                                  EVENT_MEDIA_OPTIONS.VIDEO
                                  ? EVENT_MEDIA_OPTIONS.VIDEO
                                  : EVENT_MEDIA_OPTIONS.IMAGE,
                              showTitle: adData?.showTitle,
                              showDescription: adData?.showDescription,
                              // Map API position names to banner component prop names
                              namePosition: adData?.namePosition,
                              descriptionPosition: adData?.descriptionPosition,
                              linkPosition: adData?.linkPosition,
                              sponsorLogoPosition: adData?.sponsorLogoPosition,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-ss-neutral-50 bg-white px-6 py-4 shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)] rounded-bl-none rounded-br-none lg:rounded-bl-xl lg:rounded-br-xl">
                <div className="flex justify-end">
                  <DrawerClose asChild>
                    <Button aria-label="Close advertisement details drawer">
                      OK
                    </Button>
                  </DrawerClose>
                </div>
              </div>
            </>
          )}
        </DrawerContent>
      </Drawer>
      <UserActionDialog
        open={userActionDialogOpen}
        onOpenChange={setUserActionDialogOpen}
        showForm={false}
        rejectAdReason={userActionType === AdvertisementActions.reject}
        danger={dialogDanger}
        title={dialogTitle}
        subtext={dialogSubtext}
        confirmButtonText={dialogButton}
        isLoading={isUpdatingAdvertisementStatus || isUpdatingAdvertisementACK}
        onConfirm={formData => {
          if (userActionType) {
            const reason =
              userActionType === AdvertisementActions.reject && formData.reason
                ? formData.reason
                : undefined
            handleActionClick(userActionType, reason)
          }
        }}
      />
      <EditAdPlacementDrawer
        open={editAdplacementDrawerOpen}
        onOpenChange={setEditAdplacementDrawerOpen}
        adId={adId}
        sponsorShipId={adData?.sponsorshipId ?? ''}
      />

      {/* Full Screen Hero Banner Preview Dialog */}
      {adData?.placement === 'hero' && (
        <Dialog
          open={isFullScreenPreviewOpen}
          onOpenChange={setIsFullScreenPreviewOpen}
        >
          <DialogContent className="min-w-[80%] max-w-[1080px] p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-ss-neutral-100">
              <DialogTitle>Hero Banner - Full Screen Preview</DialogTitle>
              <DialogDescription>
                Viewing the hero banner at its full size for better detail.
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 py-6">
              {BannerComponent && (
                <BannerComponent
                  data={{
                    adName: adData?.name,
                    adDescription: adData?.description,
                    linkText: adData?.linkText,
                    redirectionLink: adData?.link,
                    sponsorLogoUrl: adData?.sponsorLogo?.url || null,
                    eventImageUrl: adData?.eventImage?.url || null,
                    eventMediaType:
                      adData?.eventImage &&
                      getMediaType(adData.eventImage.key) ===
                        EVENT_MEDIA_OPTIONS.VIDEO
                        ? EVENT_MEDIA_OPTIONS.VIDEO
                        : EVENT_MEDIA_OPTIONS.IMAGE,
                    showTitle: adData?.showTitle,
                    showDescription: adData?.showDescription,
                    // Map API position names to banner component prop names
                    namePosition: adData?.namePosition,
                    descriptionPosition: adData?.descriptionPosition,
                    linkPosition: adData?.linkPosition,
                    sponsorLogoPosition: adData?.sponsorLogoPosition,
                  }}
                />
              )}
            </div>
            <DialogFooter className="px-6 pb-6 pt-0 border-t border-ss-neutral-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsFullScreenPreviewOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
